// @flow

import _ from 'lodash';

import ApiObject from '../ApiObject';
import ArrayUtils from '../../utils/ArrayUtils';

import MenuPartProduct from './MenuPartProduct';
import MenuPartCategory from './MenuPartCategory';

import { getLocalizedData } from '../../utils/CatalogUtils'

export default class MenuPart extends ApiObject {
  id: string = '';

  name: string = '';

  localizedNames: Array<> = [];

  max: number = 1;

  min: number = 1;

  additionalPrice: number = 0;

  baseAdditionalPrice: number = 0;

  products: Array<MenuPartProduct> = [];

  categories: Array<MenuPartCategory> = [];

  constructor() {
    super();
    this.createDefaultCategory();
  }

  set name(name) {
    this._name = name
  }

  get name() {
    return getLocalizedData(this.localizedNames) || this._name || ''
  }

  postJsonAssignation(data: Object) {
    // TODO: postJsonAssignation
    this.products = [];
    _.each(data.products, (partProductData: Object) => {
      const partProduct: MenuPartProduct = new MenuPartProduct();
      partProduct.fromJson(partProductData);
      this.products.push(partProduct);
    });

    this.categories = [];
    _.each(data.categories, categoryData => {
      const category: MenuPartCategory = new MenuPartCategory();
      category.fromJson(categoryData);
      this.categories.push(category);
    });
  }

  createDefaultCategory() {
    const category = new MenuPartCategory();
    category.id = MenuPartCategory.ROOT_CATEGORY_ID;
    category.name = MenuPartCategory.ROOT_NAME;
    category.items = [];
    this.categories.push(category);
  }

  //
  // Validation
  //

  /**
   * Verify that the object is correct
   */
  isValid(): boolean {
    return (
      this.validateName() &&
      this.validateMin() &&
      this.validateMax() &&
      this.validateProducts() &&
      this.validateAdditionalPrice()
    );
  }

  isValidToCreate(): boolean {
    return (
      this.validateName() &&
      this.validateMin() &&
      this.validateMax() &&
      // && this.validateProducts()
      this.validateAdditionalPrice()
    );
  }

  validateName(): boolean {
    if (_.isEmpty(this.localizedNames)) {
      return false
    }

    let isValid = false

    _.forEach(this.localizedNames, localizedName => {
      if (!_.isEmpty(localizedName.text)) {
        isValid = true
      }
    })

    return isValid
  }

  validateMin(): boolean {
    return (
      !_.isNil(this.min) &&
      !_.isNaN(this.min) &&
      this.min >= 0 &&
      this.min <= this.max
    );
  }

  validateMax(): boolean {
    return (
      !_.isNil(this.max) &&
      !_.isNaN(this.max) &&
      this.max >= 0 &&
      this.max >= this.min
    );
  }

  validateProducts(): boolean {
    return !_.isEmpty(this.products);
  }

  validateAdditionalPrice(): boolean {
    return this.additionalPrice >= 0;
  }

  static validationErrors = {
    name: 'Le nom ne peut être vide',
    min: 'Le minimum doit être positif et inférieur ou égal au maximum',
    max: 'Le maximum doit être positif et supérieur ou égal au minimum',
    products: 'Il faut au moins un produit',
    additionalPrice: 'Le prix additionnel doit être positif',
  };

  getValidationErrors(): Object {
    const validationErrors = MenuPart.validationErrors;
    const errors = {};

    if (!this.validateName()) {
      errors.name = validationErrors.name;
    }

    if (!this.validateMin()) {
      errors.min = validationErrors.min;
    }

    if (!this.validateMax()) {
      errors.max = validationErrors.max;
    }

    if (!this.validateProducts()) {
      errors.products = validationErrors.products;
    }

    if (!this.validateAdditionalPrice()) {
      errors.additionalPrice = validationErrors.additionalPrice;
    }

    return errors;
  }

  //
  //
  //

  getRootCategory() {
    return ArrayUtils.find(
      this.categories,
      category => category.id === MenuPartCategory.ROOT_CATEGORY_ID
    );
  }

  getCategoryWithId(id: string): MenuPartCategory {
    return ArrayUtils.find(this.categories, category => category.id === id);
  }

  addCategory(category) {
    this.categories.push(category);
  }

  cleanCategories() {
    this.categories = this.categories.map(category => {
      category.items = category.items
        .map(item => {
          if (item.isCategory()) {
            return item;
          }

          // is product, exists on part?
          if (ArrayUtils.exists(this.products, p => p.id === item.id)) {
            // exists. keep it.
            return item;
          }

          return null;
        })
        .filter(a => a !== null);
      return category;
    });
  }

  updateCategory(categoryItem) {
    this.categories = ArrayUtils.updateObject(
      this.categories,
      categoryItem,
      category => category.id === categoryItem.id
    );
  }

  deleteCategory(categoryItem) {
    this.categories = ArrayUtils.deleteObject(
      this.categories,
      category => category.id === categoryItem.id
    );
  }

  //
  //
  //

  findPartProductById(id: string): ?MenuPartProduct {
    return ArrayUtils.find(this.products, { id });
  }

  havePartProductById(id: string): boolean {
    return ArrayUtils.exists(this.products, { id });
  }

  //
  //
  //

  addProducts(products) {
    const partProducts = [];

    // create new MenuPartProduct array
    _.each(products, (product: Product) => {
      const partProduct = new MenuPartProduct();
      partProduct.id = product.id;

      // search for already set MenuPartProduct for the Product.
      // we don't want to erase the additionalPrice.
      const found = ArrayUtils.find(this.products, { id: product.id });
      if (!_.isNull(found)) {
        partProduct.additionalPrice = found.additionalPrice;
      }

      partProducts.push(partProduct);

      // product is added, we add it to the root category
      if (_.isNull(found)) {
        this.getRootCategory().addProduct(partProduct);
      }
    });

    this.products = partProducts;
    this.cleanCategories();
  }

  addProduct(product) {
    this.products.push(product);
    this.getRootCategory().addProduct(product);
  }

  deleteProduct(productToDelete: MenuPartProduct) {
    this.products = ArrayUtils.removeObject(this.products, {
      id: productToDelete.id,
    });

    this.categories = this.categories.map(category => {
      category.removeProductWithId(productToDelete.id);
      return category;
    });
  }

  updateProduct(productToUpdate: MenuPartProduct) {
    this.products = ArrayUtils.updateObject(this.products, productToUpdate, {
      id: productToUpdate.id,
    });
  }
}



// WEBPACK FOOTER //
// ./sdk/scripts/entities/menu/MenuPart.js